import { ConfigurationValues as ConfigurationsType } from '@cutr/constants/windows/windows.ts';
import {
  CONFIGURATIONS_OPTIONS,
  ConfigurationsOptions,
  SystemClass,
  SystemType,
} from '@cutr/constants/windows/windows-configurations.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/windows/primitives/Label';
import { Select, SelectItem } from '@/windows/primitives/Select';

import { ConfigurationDisplay } from '../Configurations';
import { useConfigurations, useResolvedSystem } from '../ConfigurationsContext';
import { ConfigurationsAction } from '../ConfigurationsContext';

type FilterProps = { systemTypes: SystemType[]; systemClasses?: SystemClass[] };

export type OptionsFilterFn = (
  option: FilterProps,
  configurations: Partial<ConfigurationsType>
) => boolean | undefined;

type ConfigurationSelectInputProps<K extends keyof ConfigurationsOptions> = {
  configurationKey: K;
  dispatchAction: ConfigurationsAction['type'];
  optionsFilterFn?: OptionsFilterFn;
};

export const filterConfigurationOptions = <A extends readonly object[]>({
  options,
  selectedValue,
  configurations,
  optionsFilterFn,
}: {
  options: A;
  selectedValue: unknown;
  configurations: Partial<ConfigurationsType>;
  optionsFilterFn?: OptionsFilterFn;
}): A[number][] =>
  (options as readonly A[number][]).filter((option) => {
    if (
      'deprecated' in option &&
      option.deprecated &&
      'value' in option &&
      option.value !== selectedValue
    ) {
      return false;
    }
    return optionsFilterFn
      ? optionsFilterFn(option as FilterProps, configurations)
      : true;
  });

export const ConfigurationSelect = <K extends keyof ConfigurationsOptions>({
  configurationKey,
  dispatchAction,
  optionsFilterFn,
}: ConfigurationSelectInputProps<K>) => {
  const { t } = useTranslation();
  const { configurations, dispatch } = useConfigurations();
  const system = useResolvedSystem();
  const labelId = React.useId();
  const inputId = React.useId();

  const currentConfiguration = CONFIGURATIONS_OPTIONS[configurationKey];

  if (!('options' in currentConfiguration)) {
    return null;
  }

  const optionsForCurrentConfigurationKey = currentConfiguration.options;
  // @ts-expect-error: not all configuration values have a `value` property
  const selectedValue = configurations[configurationKey]?.value;

  const options = filterConfigurationOptions({
    options: optionsForCurrentConfigurationKey,
    selectedValue,
    configurations: { ...configurations, system },
    optionsFilterFn,
  });

  return (
    <>
      <Label id={labelId} htmlFor={inputId}>
        {t(`windows.configurations.${configurationKey}.label`)}
      </Label>
      <Select
        aria-labelledby={labelId}
        id={inputId}
        items={options}
        // @ts-expect-error: this errors because of the configurations that don't use this component
        selectedKey={configurations[configurationKey]?.value}
        onSelectionChange={(selected) => {
          const value = options.find((option) => option.value === selected)!;
          dispatch({
            type: dispatchAction,
            // @ts-expect-error: typing is hard
            value: value,
          });
        }}
      >
        {(option) => (
          <SelectItem
            id={option.value.toString()}
            textValue={option.value.toString()}
          >
            {/* @ts-expect-error: typing is hard */}
            <ConfigurationDisplay
              configurationKey={configurationKey}
              value={option}
            />
          </SelectItem>
        )}
      </Select>
    </>
  );
};

export const optionFilterBySystem = (
  option: { systemTypes: SystemType[] },
  configurations: Partial<ConfigurationsType>
) => {
  // don't filter if system is not set.
  if (!configurations.system) {
    return true;
  }
  return (
    configurations.system &&
    option.systemTypes.includes(configurations.system.systemType)
  );
};

export const optionFilterBySystemClass = (
  option: { systemClasses?: SystemClass[] },
  configurations: Partial<ConfigurationsType>
): boolean => {
  if (!option.systemClasses || !configurations.system) {
    return true;
  }
  return option.systemClasses.includes(configurations.system.value);
};

export const optionFilterBySystemAndClass = (
  option: { systemTypes: SystemType[]; systemClasses?: SystemClass[] },
  configurations: Partial<ConfigurationsType>
): boolean =>
  optionFilterBySystem(option, configurations) &&
  optionFilterBySystemClass(option, configurations);
